import { defineComponent as _defineComponent } from 'vue'
import type { TitleFilter } from '@/@types/graphql-types';
import type { Content } from '@/interfaces/components/content';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type { BucketData, FormattedTimelineData } from '@/interfaces/new-title-graphql';
import type {
	GetNewTitleBucketsQuery,
	GetNewTitleBucketsQueryVariables,
} from '@/pages/graphql/queries/GetNewTitleBuckets.query';

import { CollectionType } from '@/enums/collection-type';
import { GetNewTitleBucketsDocument } from '@/pages/graphql/queries/GetNewTitleBuckets.query';
import { MonetizationType, NewPageType, ObjectType } from '@/@types/graphql-types';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { trackTitleEventGraphql } from '@/helpers/title-helper';
import { hasQueryParamFilters } from '@/helpers/filter-helper';
import { toastError } from '@/helpers/toast/toast-helper';
import { groupBy } from '@/helpers/object-helper';
import { isEqual } from 'apollo-utilities';

import {
	useFilterStore,
	useLanguageStore,
	useMetaStore,
	useRoutingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';
import { useRoute, onBeforeRouteLeave } from 'vue-router/composables';

import { computed, ref, watch } from 'vue';

import { FilterBar } from '@/lazy-load-components';
import ContentHeader from '@/components/content-header/ContentHeader.vue';
import TitleTimeline from '@/components/timeline/TitleTimeline.vue';

type GroupedItems = {
	[key: string]: BucketData[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'New',
  setup(__props) {

const NewProBanner = () => import('@/components/NewProBanner.vue');
const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');
const NewPageTypeSwitcher = () =>
	import(/* webpackChunkName: "new-page-switcher" */ '@/components/nav/switcher/NewPageTypeSwitcher.vue');
const NewPageTypeSwitcherSeo = () =>
	import(/* webpackChunkName: "new-page-switcher-seo" */ '@/components/nav/switcher/seo/NewPageTypeSwitcherSeo.vue');
const FallbackTitles = () =>
	import(/* webpackChunkName: "fallback-titles" */ '@/components/timeline/FallbackTitles.vue');

const filterStore = useFilterStore();
const languageStore = useLanguageStore();
const routingStore = useRoutingStore();
const userStore = useUserStore();
const metaStore = useMetaStore();

const showFooter = ref(false);

const isFirstPageLoaded = ref<Record<NewPageType, boolean>>({
	[NewPageType.New]: false,
	[NewPageType.LeavingSoon]: false,
	[NewPageType.Upcoming]: false,
	[NewPageType.PriceDrop]: false,
});

onBeforeRouteLeave((_to, _from, next) => {
	if (process.client) {
		const count = metaStore.visiblePosters.value || 0;
		if (count > 0) {
			metaStore.setVisiblePosters(0);
		}
	}

	next();
});

function handlePassUpTitles(newTitles: any[]) {
	metaStore.setNewPageJsonLD(newTitles);

	if (process.server) {
		const increment = (newTitles || []).length;
		metaStore.setVisiblePosters(metaStore.visiblePosters.value + increment);
	}
}

const filtersNew = computed(() => filterStore.filters.value(CollectionType.NEW));

const titleTimeline = ref<InstanceType<typeof TitleTimeline> | null>(null);

watch(filtersNew, (newValue, oldValue) => {
	if (isEqual(newValue, oldValue)) return;

	isFirstPageLoaded.value[newPageType.value] = false;
	titleTimeline.value?.resetInfiniteScroll();

	// scroll to top if the filters have changed
	scrollToTop();
});

const graphqlFilters = computed<TitleFilter>(() => {
	const selectedProviders = filterStore.selectedProviders.value(!userStore.isLoggedIn.value);
	return toGraphqlFilters(filtersNew.value, selectedProviders, userStore.isHideSeenDislikedTitlesEnabled.value);
});

// We need this mapper due to filter naming on the New Page
// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
const filterObjectTypes = computed(() => {
	return graphqlFilters.value?.objectTypes?.map(objectType =>
		objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
	);
});

const route = useRoute();
const newPageType = computed(() => route?.meta?.newPageType as NewPageType);

const isLeavingOrComingSoon = computed(
	() => newPageType.value === NewPageType.LeavingSoon || newPageType.value === NewPageType.Upcoming
);

const queryVariables = computed(() => {
	const hasQueryParams = hasQueryParamFilters(filtersNew.value);
	return {
		country: languageStore.country.value,
		newAfterCursor: '',
		newTitlesFilter: {
			...graphqlFilters.value,

			// point is to exclude MonetizationType.Fast from New
			monetizationTypes: graphqlFilters.value.monetizationTypes?.filter(
				monetizationType => monetizationType !== MonetizationType.Fast
			),
			objectTypes: filterObjectTypes.value,
		},
		priceDrops: newPageType.value === NewPageType.PriceDrop,
		bucketSize: 0,
		pageType: newPageType.value,

		// besides isLeavingOrComingSoon && !isPremium, we want to limit the amount of buckets when a request comes in with query parameters.
		// these query parameter pages usually have a canonical and are not interesting to Google,
		// but they take some time to SSR on-demand. and this is reflected in Google's crawl stats.
		first: isLeavingOrComingSoon.value && !userStore.isPremium.value ? 1 : hasQueryParams ? 4 : 8,
	};
});

const {
	result,
	loading: isTitlesLoading,
	onResult,
	onError,
	fetchMore,
} = useQuery<GetNewTitleBucketsQuery, GetNewTitleBucketsQueryVariables>(
	GetNewTitleBucketsDocument,
	queryVariables,
	() => ({ enabled: newPageType.value != null })
);

onResult(() => (isFirstPageLoaded.value[newPageType.value] = true));

onError(error => {
	if (process.server) return;

	toastError({ message: 'Something went wrong, please try again' });
	captureMessageForSentry('[GraphQL New Page Load Issue]:', { error, where: 'Component: New' }, 'error');
});

const newPageData = computed(() => result.value?.newTitleBuckets);

async function onLoadNext({ event }: { event: { target?: { complete: (len: number) => void } } }) {
	if (event.target?.complete == null) return;

	try {
		await fetchMore({
			variables: { newAfterCursor: newPageData.value?.pageInfo.endCursor },
			updateQuery(previousResult, { fetchMoreResult }) {
				const fetchedBuckets = fetchMoreResult?.newTitleBuckets;
				const previousBuckets = previousResult.newTitleBuckets;

				if (fetchedBuckets?.edges == null || previousBuckets.edges == null) return previousResult;

				previousResult.newTitleBuckets.pageInfo = fetchedBuckets.pageInfo;
				previousResult.newTitleBuckets.edges?.push(...fetchedBuckets.edges);

				return previousResult;
			},
		});
	} catch (error) {
		console.error(error);
		captureMessageForSentry(
			'[GraphQL New Page Fetch More Issue]:',
			{ error: error as Error, where: 'Component: New' },
			'error'
		);
	} finally {
		event.target.complete((newPageData.value?.edges ?? []).length);
	}
}

const showNewProBanner = computed(
	() => !userStore.isPremium.value && isLeavingOrComingSoon.value && !isTitlesLoading.value
);

const formattedTimelineData = computed<FormattedTimelineData[]>(() => {
	const groupedBuckets = groupBy(newPageData.value?.edges ?? [], item => {
		if (isLeavingOrComingSoon.value)
			return (item.key as AssertByTypename<(typeof item)['key'], 'BucketPackageAggregationKey'>).bucketType;

		return (item.key as AssertByTypename<(typeof item)['key'], 'DatePackageAggregationKey'>).date;
	}) as GroupedItems;

	if (!groupedBuckets) return [];
	return Object.keys(groupedBuckets ?? '').reduce((newPageDays, key, currentIndex) => {
		/*
		 Due to a race condition when changing between Leaving soon/Coming soon and New/Pricedrops
		 groupBy() produces an object with a property "undefined" (as a string, not a type), that then used as a key for buckets.
		 It causes some visual artifacts and wrong GraphQL requests when switch between pages. This hack prevents it.
		*/
		if (key === 'undefined') {
			return [];
		}

		/*
		 * Show only first bucket to non pro users
		 * */
		if (!userStore.isPremium.value && currentIndex > 0 && isLeavingOrComingSoon.value) {
			return newPageDays;
		}

		return [
			...newPageDays,
			{
				key,
				isGroupedByDate: !isLeavingOrComingSoon.value,
				providers:
					isLeavingOrComingSoon.value && !userStore.isPremium.value
						? groupedBuckets[key].slice(0, 1)
						: groupedBuckets[key],
			},
		];
	}, [] as FormattedTimelineData[]);
});

// PERCEIVED LOADING START
const perceivedLoading = ref(false);

// if we return to a page, that we fetched before via graphql, we don't trigger onLoadingChange.
// that's what this watcher does.
watch(routingStore.activeRoute, () => {
	if (process.client) {
		const { collectionType } = routingStore.activeRoute.value?.meta as any;
		const { collectionType: lastCollectionType } = routingStore.lastRoute.value?.meta as any;
		// only work when the page type is NEW
		// and the last pageType was also NEW
		if (collectionType === CollectionType.NEW && lastCollectionType === CollectionType.NEW) {
			perceivedLoading.value = true;
			setTimeout(() => {
				perceivedLoading.value = false;
			}, 0);
		}
	}
});

const showSpinner = computed(() => !isFirstPageLoaded.value[newPageType.value] && isTitlesLoading.value);

watch(showSpinner, () => {
	// just to be sure, that SSR is never rendering the loading state
	if (process.client) {
		// add a small time, so the vue runtime gets time to render the loading state.
		// if there is no loading state, vue will have to update the existing components with the new virtual DOM. this is more compute intense.
		if (!!showSpinner.value) perceivedLoading.value = true;

		// using setTimeout instead of $nextTick, because $nextTick will be optimised by Vue into one render.
		// but we need to force an additional render with the loading state.
		if (!showSpinner.value) setTimeout(() => (perceivedLoading.value = false), 0);
	}
});
// PERCEIVED LOADING END

const isAtEndOfList = computed(() => !newPageData.value?.pageInfo?.hasNextPage || showNewProBanner.value);

const content = ref<Content | null>(null);
function scrollToTop() {
	content.value?.scrollToTop();
}

function trackClick(payload: TitleEventPayloadGraphql) {
	trackTitleEventGraphql('title_clicked', payload, 'new-timeline');
}

return { __sfc: true,NewProBanner, FilterBarSeo, NewPageTypeSwitcher, NewPageTypeSwitcherSeo, FallbackTitles, filterStore, languageStore, routingStore, userStore, metaStore, showFooter, isFirstPageLoaded, handlePassUpTitles, filtersNew, titleTimeline, graphqlFilters, filterObjectTypes, route, newPageType, isLeavingOrComingSoon, queryVariables, result, isTitlesLoading, onResult, onError, fetchMore, newPageData, onLoadNext, showNewProBanner, formattedTimelineData, perceivedLoading, showSpinner, isAtEndOfList, content, scrollToTop, trackClick, CollectionType, FilterBar, ContentHeader, TitleTimeline }
}

})